import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ContentIcon } from '@/components/atoms';
import { type IconKey } from '@/content/site';

import { RAIL_ACCENT_INK, RAIL_LABEL_INK } from './pageIntroInk';

/**
 * One proof point. Short enough to hold a single line in the strip it lives
 * in — these are labels, not sentences. Anything that needs a clause belongs
 * in the lede or in a band further down.
 *
 * Below `md` that is no longer a style note but a constraint: the claim is a
 * chip in a row that scrolls sideways, and a chip that wraps is not a chip.
 * The longest in the set today is "Every URA charge itemised" at 25
 * characters, which holds its line comfortably.
 */
export type PageIntroHighlight = { icon: IconKey; label: string };

type Props = {
  item: PageIntroHighlight;
  /** The band is wearing its stage, so from `md` up this claim is on glass. */
  stage: boolean;
};

/**
 * A single claim: icon, then label, on one line.
 *
 * Split out from the strip because the strip's job is the row — the dividers,
 * the scroll behaviour and the surface — and this one's is the pairing of a
 * mark with its text. They change for different reasons: the row changes when
 * the band's proportions do, and this changes when the ink does.
 *
 * Below `md` it also carries its own edge. Up there the pane around the whole
 * strip is what separates the claims from the band, and the dividers separate
 * them from each other; down here there is no pane and no divider, so each
 * claim has to be its own object or three labels in a sideways row read as one
 * broken sentence.
 *
 * The icon is decorative (`ContentIcon` sets `aria-hidden` itself); the label
 * carries the whole meaning, so a strip read aloud loses nothing.
 */
export function PageIntroHighlightItem({ item, stage }: Props) {
  return (
    <Stack
      component="li"
      direction="row"
      alignItems="center"
      spacing={{ xs: 2, md: 3 }}
      sx={{
        // No `flex`, deliberately. Given an equal share of the row each item
        // would be a third of the strip with its text jammed to the left of
        // it, which puts the rule that follows hard against the *next* label
        // rather than between the two — the gap it is supposed to be dividing
        // would sit entirely on one side of it. Shrink-to-fit instead, and let
        // the row distribute what is left over. `minWidth: 0` so a long claim
        // wraps rather than pushing the strip wider than the band.
        minWidth: 0,
        // And never shrink below `md`, where the row scrolls instead of
        // fitting: a chip squeezed to fit three into 390px is three ellipses.
        flexShrink: { xs: 0, md: 1 },
        // The chip, below `md` only. `background.paper` rather than the band's
        // own `background.default`, so it reads as laid on the band in both
        // colour schemes — a chip the same colour as what it sits on is a
        // rectangle of border.
        px: { xs: 3, md: 0 },
        py: { xs: 2, md: 0 },
        border: { xs: 1, md: 0 },
        borderColor: 'divider',
        borderRadius: { xs: 2, md: 0 },
        bgcolor: { xs: 'background.paper', md: 'transparent' },
        scrollSnapAlign: { xs: 'start', md: 'none' },
      }}
    >
      <ContentIcon
        name={item.icon}
        // Non-text contrast still applies to an icon (WCAG 1.4.11, 3:1); see
        // pageIntroInk.ts for why the glass accent is not the band's own red,
        // and why below `md` it is.
        sx={{ flexShrink: 0, fontSize: 21, color: stage ? RAIL_ACCENT_INK : 'primary.main' }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          lineHeight: 1.45,
          // One line in the rail, free to wrap in the strip.
          whiteSpace: { xs: 'nowrap', md: 'normal' },
          color: stage ? RAIL_LABEL_INK : 'text.secondary',
        }}
      >
        {item.label}
      </Typography>
    </Stack>
  );
}
